/* import __COLOCATED_TEMPLATE__ from './sensor-selection.hbs'; */
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { numberToUserDisplay } from '@ebiz/pretty-num';
import { DeviceColumn, DeviceData } from '@ebiz/ember-aditools-device-table/components/aditools-device-table';
import DeviceTableComparator from '@ebiz/ember-aditools-device-table/utils/comparator';
import DeviceTableFormatter from '@ebiz/ember-aditools-device-table/utils/formatter';
import type { SensorData } from '../../models/sensor';
import PartialTextFilter from '@ebiz/ember-aditools-device-table/utils/partial-text-filter';
import RangeFilter from '@ebiz/ember-aditools-device-table/utils/range-filter';
import EngineeringRangeValueFilter from '@ebiz/ember-aditools-device-table/utils/engineering-range-value-filter';
import type IntlService from 'ember-intl/services/intl';

interface SensorSelectionArgs {
  title: string;
  close: () => void;
  selectedPart: string;
  sensorData: SensorData[];
  selectSensor: (partNumber: string) => void;
}

const options = {
  asyncEditorLoading: true,
  forceFitColumns: false,
  showHeaderRow: true,
  headerRowHeight: 30,
  explicitInitialization: true,
  autoHeight: false,
  multiColumnSort: true,
  multiSelect: false,
  enableColumnReorder: false,
  enableAddRow: false,
  frozenColumn: 0,
};

export default class SensorSelection extends Component<SensorSelectionArgs> {
  @service declare intl: IntlService;
  @tracked selectEnabled = false;
  selectedPart?: string;

  get data(): SensorDataItem[] {
    return this.buildSensorSelectionData(this.args.sensorData);
  }

  get columns(): DeviceColumn<DeviceData>[] {
    return this.getSensorSelectionColumns();
  }

  get options(): { [x: string]: boolean | number } {
    return options;
  }

  getSensorSelectionColumns(): DeviceColumn<DeviceData>[] {
    return [
      {
        id: 'part',
        field: 'part',
        name: this.intl.t('sensor-selection.part-number'),
        sortable: true,
        resizable: false,
        width: 140,
        comparator: DeviceTableComparator.textComparator,
        filter: new PartialTextFilter(),
      },
      {
        id: 'manufacturer',
        field: 'manufacturer',
        name: this.intl.t('sensor-selection.manufacturer'),
        sortable: true,
        width: 121,
      },
      {
        id: 'packageType',
        field: 'packageType',
        name: this.intl.t('sensor-selection.package'),
        sortable: true,
        width: 95,
      },
      {
        id: 'spectralRange',
        field: 'spectralRange',
        name: this.intl.t('sensor-selection.spectral-range') + ' (' + this.intl.t('units.meters') + ')',
        sortable: true,
        width: 160,
        comparator: DeviceTableComparator.rangeComparator,
        filter: new RangeFilter('EQ'),
      },
      {
        id: 'spectralPeak',
        field: 'spectralPeak',
        name: this.intl.t('sensor-selection.spectral-peak') + ' (' + this.intl.t('units.meters') + ')',
        sortable: true,
        width: 190,
        comparator: DeviceTableComparator.engineeringComparator,
        filter: new EngineeringRangeValueFilter('', ''),
      },
      {
        id: 'sensitivity',
        field: 'sensitivity',
        name: this.intl.t('sensor-selection.sensitivity') + ' (' + this.intl.t('units.sensitivity') + ')',
        sortable: true,
        width: 140,
        comparator: DeviceTableComparator.engineeringComparator,
        filter: new EngineeringRangeValueFilter('', ''),
      },
      {
        id: 'darkCurrent',
        field: 'darkCurrent',
        name: this.intl.t('sensor-selection.dark-current') + ' (' + this.intl.t('units.A') + ')',
        sortable: true,
        width: 153,
        comparator: DeviceTableComparator.engineeringComparator,
        filter: new EngineeringRangeValueFilter('', ''),
      },
      {
        id: 'capacitance',
        field: 'capacitance',
        name: this.intl.t('sensor-selection.capacitance') + ' (' + this.intl.t('units.farad') + ')',
        sortable: true,
        width: 153,
        comparator: DeviceTableComparator.engineeringComparator,
        filter: new EngineeringRangeValueFilter('', ''),
      },
      {
        id: 'resistance',
        field: 'resistance',
        name: this.intl.t('sensor-selection.resistance') + ' (' + this.intl.t('units.ohm') + ')',
        sortable: true,
        width: 153,
        comparator: DeviceTableComparator.engineeringComparator,
        filter: new EngineeringRangeValueFilter('', ''),
      },
      {
        id: 'responseTime',
        field: 'responseTime',
        name: this.intl.t('sensor-selection.response-time') + ' (' + this.intl.t('units.seconds') + ')',
        sortable: true,
        width: 162,
        comparator: DeviceTableComparator.engineeringComparator,
        filter: new EngineeringRangeValueFilter('', ''),
      },
      {
        id: 'reverseBreakdown',
        field: 'reverseBreakdown',
        name: this.intl.t('sensor-selection.reverse-breakdown') + ' (' + this.intl.t('units.V') + ')',
        sortable: true,
        width: 205,
        comparator: DeviceTableComparator.engineeringComparator,
        filter: new EngineeringRangeValueFilter('', ''),
      },
      {
        id: 'activeArea',
        field: 'activeArea',
        name: this.intl.t('sensor-selection.active-area') + ' (' + this.intl.t('units.active-area') + ')',
        sortable: true,
        width: 180,
        comparator: DeviceTableComparator.engineeringComparator,
        filter: new EngineeringRangeValueFilter('', ''),
      },
      {
        id: 'viewingAngle',
        field: 'viewingAngle',
        name: this.intl.t('sensor-selection.viewing-angle') + ' (' + this.intl.t('units.viewing-angle') + ')',
        sortable: true,
        width: 145,
        comparator: DeviceTableComparator.engineeringComparator,
        filter: new EngineeringRangeValueFilter('', ''),
      },
      {
        id: 'operatingTemperature',
        field: 'operatingTemperature',
        name:
          this.intl.t('sensor-selection.operating-temperature') +
          ' (' +
          this.intl.t('units.operating-temperature') +
          ')',
        sortable: true,
        width: 210,
        comparator: DeviceTableComparator.rangeComparator,
        filter: new RangeFilter('EQ'),
      },
      {
        id: 'spiceFileUrl',
        field: 'spiceFileLink',
        name: this.intl.t('sensor-selection.spice-file'),
        formatter: DeviceTableFormatter.linkFormatter as Slick.Formatter<DeviceData>,
        sortable: false,
        width: 110,
      },
      {
        id: 'vendorLink',
        field: 'vendorLink',
        name: this.intl.t('sensor-selection.vendor-link'),
        sortable: false,
        formatter: DeviceTableFormatter.linkFormatter as Slick.Formatter<DeviceData>,
        width: 210,
      },
    ];
  }

  buildSensorSelectionData(sensorData: SensorData[]): SensorDataItem[] {
    const data: SensorDataItem[] = [];
    for (const [index, sensor] of sensorData.entries()) {
      data.push({
        id: 'row_' + index,
        part: sensor.partNumber,
        cost: sensor.cost,
        manufacturer: sensor.manufacturer,
        packageType: sensor.packageType,
        spectralRange: sensor.spectralRange,
        spectralPeak: numberToUserDisplay(sensor.spectralPeak as number, 'Engineering', 3, { showZeroes: false }),
        sensitivity: numberToUserDisplay(sensor.sensitivity as number, 'Engineering', 3, { showZeroes: false }),
        darkCurrent: numberToUserDisplay(sensor.darkCurrent as number, 'Engineering', 3, { showZeroes: false }),
        capacitance: numberToUserDisplay(sensor.capacitance as number, 'Engineering', 3, { showZeroes: false }),
        resistance: numberToUserDisplay(sensor.resistance as number, 'Engineering', 3, { showZeroes: false }),
        responseTime: numberToUserDisplay(sensor.responseTime as number, 'Engineering', 3, { showZeroes: false }),
        reverseBreakdown: sensor.reverseBreakdown,
        activeArea: sensor.area,
        viewingAngle: sensor.angle,
        operatingTemperature: sensor.operatingTemp,
        spiceFileLink: {
          name: this.intl.t('sensor-selection.spice'),
          url: sensor.spiceFileUrl,
        },
        vendorLink: {
          name: this.intl.t('sensor-selection.product-page'),
          url: sensor.vendorLink,
        },
      });
    }
    return data;
  }

  @action partSelected(partName: string): void {
    this.selectedPart = partName;
    this.selectEnabled = true;
  }

  @action applyAndClose(closeDialog: () => void) {
    if (this.selectedPart) {
      this.args.selectSensor(this.selectedPart);
      closeDialog();
    }
  }
}
